import { useState } from 'react';
import { X, Info, Save } from 'lucide-react';
import { performanceService, AgentPerformanceRating } from '../../services/performance.service';

interface MetricWeightsModalProps {
  organizationId: string;
  currentWeights?: AgentPerformanceRating['metricWeights'];
  onClose: () => void;
  onSuccess: () => void;
}

export default function MetricWeightsModal({
  organizationId,
  currentWeights,
  onClose,
  onSuccess,
}: MetricWeightsModalProps) {
  const [weights, setWeights] = useState({
    csat: currentWeights?.csat || 0.50,
    qaRating: currentWeights?.qaRating || 0.20,
    wfmAdherence: currentWeights?.wfmAdherence || 0.10,
    occupancy: currentWeights?.occupancy || 0.10,
    productivity: currentWeights?.productivity || 0.10,

  });

  const [isSaving, setIsSaving] = useState(false);

  const totalWeight = Object.values(weights).reduce((sum, weight) => sum + weight, 0);
  const isValid = Math.abs(totalWeight - 1.0) < 0.01; // Allow small floating point errors

  const handleWeightChange = (metric: keyof typeof weights, value: number) => {
    setWeights({
      ...weights,
      [metric]: value,
    });
  };

  const handleSave = async () => {
    if (!isValid) {
      alert('Total weight must equal 100%. Please adjust the weights.');
      return;
    }

    setIsSaving(true);
    try {
      await performanceService.updateMetricWeights(organizationId, weights);
      onSuccess();
    } catch (error) {
      console.error('Failed to update metric weights:', error);
      alert('Failed to update metric weights. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setWeights({
      csat: 0.50,
      qaRating: 0.20,
      wfmAdherence: 0.10,
      occupancy: 0.10,
      productivity: 0.10,
    });
  };

  const metrics = [
    {
      key: 'wfmAdherence' as const,
      label: 'WFM Adherence',
      description: 'How well agents follow their scheduled hours',
    },
    {
      key: 'occupancy' as const,
      label: 'Occupancy',
      description: 'Percentage of time agents are productive vs available',
    },
    {
      key: 'productivity' as const,
      label: 'Productivity',
      description: 'Number of customer issues resolved per hour',
    },
    {
      key: 'csat' as const,
      label: 'CSAT Score',
      description: 'Customer satisfaction ratings from surveys',
    },

    {
      key: 'qaRating' as const,
      label: 'QA Rating',
      description: 'Quality assurance review scores',
    },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Metric Weights</h2>
            <p className="text-sm text-gray-600 mt-1">
              Adjust how each metric contributes to the overall performance score
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Info Banner */}
        <div className="p-6 bg-blue-50 border-b border-blue-100">
          <div className="flex items-start space-x-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">How it works</p>
              <p>
                Each metric weight represents its importance in calculating the overall agent
                performance score. All weights must add up to 100%.
              </p>
            </div>
          </div>
        </div>

        {/* Weights Form */}
        <div className="p-5 space-y-5">
          {metrics.map((metric) => (
            <div key={metric.key} className="space-y-2">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-900">
                    {metric.label}
                  </label>
                  <p className="text-xs text-gray-600 mt-0.5">{metric.description}</p>
                </div>
                <div className="flex items-center space-x-3 ml-4">
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={weights[metric.key]}
                    onChange={(e) => handleWeightChange(metric.key, parseFloat(e.target.value) || 0)}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-right"
                  />
                  <span className="text-sm font-medium text-gray-700 w-12">
                    {(weights[metric.key] * 100).toFixed(0)}%
                  </span>
                </div>
              </div>

              {/* Visual Weight Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all"
                  style={{ width: `${weights[metric.key] * 100}%` }}
                />
              </div>
            </div>
          ))}

          {/* Total Weight */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900">Total Weight</span>
              <div className="flex items-center space-x-3">
                <span
                  className={`text-lg font-bold ${
                    isValid ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {(totalWeight * 100).toFixed(1)}%
                </span>
                {isValid ? (
                  <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                    Valid
                  </span>
                ) : (
                  <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                    Must equal 100%
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
          >
            Reset to Defaults
          </button>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isValid || isSaving}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {isSaving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <Save className="h-4 w-4" />
              <span>{isSaving ? 'Saving...' : 'Save Weights'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
